import React, { useEffect } from 'react';
import { Box, HStack, FlatList } from 'native-base';
import {
    fakeItemsArray,
    useGetRedeemRewards,
    useGetVisits,
    useSpringBigStore,
    useTheme
} from '@springbig/shared';
import { Caption } from '~/shared/Typography';
import AccountHistoryPurchaseCard from './components/AccountHistoryPurchaseCard';
import AccountHistoryRedemptionCard from './components/AccountHistoryRedemptionCard';
import Pressable from '~/shared/Pressable';
import { useAuthorization } from '~/hooks/auth';
import { NoDataPlaceholder } from '~/shared/NoDataPlaceholder';
import { faAward, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { Section } from '~/shared/Theme';
import { DarkSkeleton } from '~/shared/DarkSkeleton';

const AccountHistoryScreen = () => {

    const {
        button_primary,
        button_text ,
    } = useTheme();

    const { member } = useSpringBigStore();

    const [toggleView, setToggleView] =
        React.useState<'purchases' | 'redemptions'>('purchases');

    const {
        data: visits,
        isLoading: isLoadingVisits,
        refetch: refetchVisits,
    } = useGetVisits();

    const {
        data: rewards,
        isLoading: isLoadingRewards,
        refetch: refetchRewards,
    } = useGetRedeemRewards();

    useEffect(() => {

        refetchVisits();
        refetchRewards();
    }, [member]);

    const { isLoadingAuth } = useAuthorization();

    const tabView = (
        <HStack
            alignItems={'center'}
            justifyContent={'space-between'}
        >
            <Pressable
                onPress={() => setToggleView('purchases')}
                flex={1}
                h={10}
                alignItems={'center'}
                justifyContent={'center'}
                bg={
                    toggleView === 'purchases'
                        ? button_primary
                        : button_primary?.slice(0, 7) + '80'
                }
            >
                <Caption
                    text="history.title_purchases"
                    color={button_text}
                />
            </Pressable>
            <Pressable
                onPress={() => setToggleView('redemptions')}
                flex={1}
                h={10}
                alignItems={'center'}
                justifyContent={'center'}
                bg={
                    toggleView === 'redemptions'
                        ? button_primary
                        : button_primary?.slice(0, 7) + '80'
                }
            >
                <Caption
                    text="history.title_redemptions"
                    color={button_text}
                />
            </Pressable>
        </HStack>
    );

    const isLoading = isLoadingVisits || isLoadingRewards || isLoadingAuth;

    if (!member || isLoading) {
        return (
            <Section>
                { tabView }
                <AccountHistoryCardSkeleton />
            </Section>
        );
    }

    if (toggleView === 'redemptions' && (!rewards || !rewards.length) && !(isLoading) && member) {
        return (
            <Section>
                { tabView }
                <NoDataPlaceholder
                    title={'common.redemptions_placeholder_title'}
                    message={'common.redemptions_placeholder_subtitle'}
                    callback={refetchRewards}
                    isLoading={isLoadingVisits}
                    iconName={faAward}
                />
            </Section>
        );
    }

    if (toggleView === 'purchases' && (!visits || !visits.length) && !(isLoading) && member) {
        return (
            <Section>
                { tabView }
                <NoDataPlaceholder
                    title={'common.purchases_placeholder_title'}
                    message={'common.purchases_placeholder_subtitle'}
                    callback={refetchVisits}
                    isLoading={isLoadingVisits}
                    iconName={faShoppingCart}
                />
            </Section>
        );
    }

    return (
        <Section>
            { tabView }
            <Box pt={4}>
                <FlatList
                    data={toggleView === 'purchases' ? visits : rewards}
                    keyExtractor={(item, index) =>
                        (toggleView === 'purchases' ? index.toString() : (index + 200).toString())
                    }
                    renderItem={({ item, index }) => {

                        if (toggleView === 'purchases') {
                            return <AccountHistoryPurchaseCard visit={item} />;
                        }

                        return <AccountHistoryRedemptionCard key={index + 200} redeemed={item} />;
                    }}
                />
            </Box>
        </Section>
    );
};

const AccountHistoryCardSkeleton = () => {

    return (
        <Box pt={4}>
            { fakeItemsArray.map((index) =>

                <DarkSkeleton key={index} h={'164px'} my={2} w='100%' />
            )}
        </Box>
    );
};

export default AccountHistoryScreen;
